import React, { useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import HealthReportPDF from '../Export/HealthReportPDF';
import { convertFxRange } from '../../util/GenericUtil';
import './ShareButton.css';

/**
 * ShareButton - Uses native share on mobile, falls back to PDF download
 *
 * Props:
 *   testDate: string | number
 *   patientInfo: object
 *   systemScores: array
 *   tests: array
 *   recommendations: array
 *   recommendationMode: 'support' | 'therapeutic'
 *   getFxRange: function
 *   variant: 'icon' | 'default'
 */
function ShareButton({
    testDate,
    patientInfo,
    systemScores,
    tests,
    recommendations = [],
    recommendationMode,
    getFxRange,
    focuses,
    assessmentSummary,
    sex,
    variant = 'default'
}) {
    const [isSharing, setIsSharing] = useState(false);

    const generatePdfBlob = async () => {
        const testsWithRanges = tests.map(test => {
            const rawFxRange = getFxRange(test.alias || test.test_name, 'male');
            const fxRange = convertFxRange(rawFxRange, test.alias || test.test_name, test.units);
            return {
                ...test,
                fx_low: fxRange?.fx_low ?? null,
                fx_high: fxRange?.fx_high ?? null
            };
        });

        return await pdf(
            <HealthReportPDF
                testDate={testDate}
                patientInfo={patientInfo}
                systemScores={systemScores}
                tests={testsWithRanges}
                recommendations={recommendations}
                recommendationMode={recommendationMode}
                focuses={focuses}
                assessmentSummary={assessmentSummary}
                sex={sex}
            />
        ).toBlob();
    };

    const getFilename = () => {
        const dateStr = testDate
            ? testDate.toString().replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')
            : new Date().toISOString().split('T')[0];
        return `health-report-${dateStr}.pdf`;
    };

    const handleShare = async () => {
        if (isSharing || !tests?.length) return;

        setIsSharing(true);
        try {
            const blob = await generatePdfBlob();
            const file = new File([blob], getFilename(), { type: 'application/pdf' });

            // Check if native share is available and supports files
            if (navigator.share && navigator.canShare?.({ files: [file] })) {
                await navigator.share({
                    title: 'Health Report',
                    text: 'Check out my health report from PrimeMaxing',
                    files: [file]
                });
            } else if (navigator.share) {
                // Share without file (URL/text only on some platforms)
                await navigator.share({
                    title: 'Health Report',
                    text: 'Check out my health report from PrimeMaxing'
                });
                // Then download the PDF as fallback
                downloadBlob(blob);
            } else {
                // Full fallback to download
                downloadBlob(blob);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error sharing:', error);
                // Fallback to download on error
                try {
                    const blob = await generatePdfBlob();
                    downloadBlob(blob);
                } catch (downloadError) {
                    console.error('Download also failed:', downloadError);
                    alert('Failed to share or download report. Please try again.');
                }
            }
        } finally {
            setIsSharing(false);
        }
    };

    const downloadBlob = (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = getFilename();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const isIcon = variant === 'icon';

    return (
        <button
            className={`share-button ${isSharing ? 'sharing' : ''} ${isIcon ? 'icon-variant' : ''}`}
            onClick={handleShare}
            disabled={isSharing || !tests?.length}
            title={isSharing ? 'Sharing...' : 'Share Report'}
            aria-label={isSharing ? 'Sharing report' : 'Share report'}
        >
            {isSharing ? (
                <div className="share-spinner" />
            ) : (
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
            )}
            {!isIcon && <span>{isSharing ? 'Sharing...' : 'Share'}</span>}
        </button>
    );
}

export default ShareButton;
